/* NeuralX Aura - theme runtime
 * Scope: theme toggle, preloader, page transitions, announcement rotator,
 * sticky header, mobile nav, scrim, reveal-on-scroll, hero parallax,
 * cart drawer (ajax), product form, sticky buy bar, newsletter,
 * cursor follower, magnetic buttons, count-up, scroll progress.
 * No external runtime dependencies required.
 */
(() => {
  "use strict";
  const $  = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => Array.from(root.querySelectorAll(s));
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isTouch = window.matchMedia("(hover: none)").matches;
  const money = (cents) => {
    if (typeof cents !== "number") return cents;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: window.theme?.shop?.currency || "USD" }).format(cents / 100);
  };

  /* ---------- THEME TOGGLE ---------- */
  function themeToggle() {
    const root = document.documentElement;
    const btns = $$("[data-theme-toggle]");
    if (!btns.length) return;
    const apply = (mode) => {
      root.setAttribute("data-theme", mode);
      try { localStorage.setItem("aura-theme", mode); } catch (e) {}
      // animate the swap
      root.style.setProperty("--theme-flash", "1");
      setTimeout(() => root.style.removeProperty("--theme-flash"), 400);
    };
    btns.forEach(btn => {
      btn.addEventListener("click", () => {
        const cur = root.getAttribute("data-theme") || "light";
        apply(cur === "dark" ? "light" : "dark");
      });
    });
    // listen for system theme changes when user has not opted manually
    try {
      const mql = window.matchMedia("(prefers-color-scheme: dark)");
      mql.addEventListener?.("change", (e) => {
        if (!localStorage.getItem("aura-theme")) {
          apply(e.matches ? "dark" : "light");
          // and remove the explicit save so we keep tracking system
          localStorage.removeItem("aura-theme");
        }
      });
    } catch (e) {}
    // remove fouc class
    requestAnimationFrame(() => root.classList.remove("theme-loading"));
  }

  /* ---------- PRELOADER ---------- */
  function preloader() {
    const el = $("[data-preloader]");
    if (!el) return;
    document.body.classList.add("is-preloading");
    const bar = $("[data-preloader-bar]", el);
    let progress = 0;
    let target = 30;
    const tick = () => {
      progress += (target - progress) * 0.18;
      if (bar) bar.style.width = progress.toFixed(2) + "%";
      if (progress < 99.4) requestAnimationFrame(tick);
    };
    tick();
    const finish = () => {
      target = 100;
      // Allow at least 700ms total even on instant loads
      setTimeout(() => {
        el.classList.add("is-done");
        document.body.classList.remove("is-preloading");
        // unobserve scroll lock; trigger any first-paint animations
        document.dispatchEvent(new CustomEvent("preloader:done"));
      }, 700);
    };
    if (document.readyState === "complete") {
      target = 90;
      setTimeout(finish, 200);
    } else {
      // ramp up gradually
      let ramp = 0;
      const rampId = setInterval(() => {
        ramp += 6;
        target = Math.min(80, ramp);
        if (target >= 80) clearInterval(rampId);
      }, 100);
      window.addEventListener("load", () => {
        clearInterval(rampId);
        finish();
      });
      // Hard fallback - never leave the user staring at a splash
      setTimeout(() => {
        if (!el.classList.contains("is-done")) finish();
      }, 3500);
    }
  }

  /* ---------- PAGE TRANSITIONS ---------- */
  function pageTransitions() {
    if (reduceMotion) return;
    const overlay = $("[data-page-transition]");
    if (!overlay) return;
    const sameDoc = (a) => {
      try {
        const u = new URL(a.href, location.href);
        return u.origin === location.origin && (u.pathname !== location.pathname || u.search !== location.search);
      } catch { return false; }
    };
    document.addEventListener("click", (e) => {
      const a = e.target.closest("a[data-nav-link], a.foot__col, .foot__col a, .header__nav a, .mobile-nav a, .header__logo");
      if (!a || !a.href) return;
      if (a.target === "_blank" || a.hasAttribute("download")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (a.href.startsWith("mailto:") || a.href.startsWith("tel:")) return;
      if (!sameDoc(a)) return;
      // hash on same page → skip overlay
      const url = new URL(a.href, location.href);
      if (url.pathname === location.pathname && url.hash) return;
      e.preventDefault();
      overlay.classList.remove("is-entering");
      overlay.classList.add("is-leaving");
      setTimeout(() => { window.location.href = a.href; }, 520);
    });
    // when bfcache restores the page, replay enter
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) {
        overlay.classList.remove("is-leaving");
        overlay.classList.add("is-entering");
        setTimeout(() => overlay.classList.remove("is-entering"), 720);
      }
    });
  }

  /* ---------- ANNOUNCEMENT BAR ---------- */
  function announcement() {
    const bar = $("[data-announce]");
    if (!bar) return;
    const msgs = $$("[data-announce-msg]", bar);
    if (msgs.length < 2) return;
    const interval = (parseInt(bar.dataset.interval, 10) || 5) * 1000;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.add("is-hidden");
      const next = (i + 1) % msgs.length;
      msgs[next].classList.add("is-incoming");
      msgs[next].classList.remove("is-hidden");
      requestAnimationFrame(() => requestAnimationFrame(() => msgs[next].classList.remove("is-incoming")));
      i = next;
    }, interval);
  }

  /* ---------- HEADER SCROLL ---------- */
  function header() {
    const el = $("[data-header]");
    if (!el) return;
    let prev = window.scrollY;
    let hidden = false;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      // Sticky-header tint kicks in at 32px per Phase 6 spec
      el.classList.toggle("is-scrolled", y > 32);
      if (y > 120 && y > prev + 4 && !hidden) { el.classList.add("is-hidden"); hidden = true; }
      else if ((y < prev - 4 || y < 80) && hidden) { el.classList.remove("is-hidden"); hidden = false; }
      prev = y;
      ticking = false;
    };
    window.addEventListener("scroll", () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ---------- MOBILE NAV ---------- */
  function mobileNav() {
    const toggle = $("[data-mobile-toggle]");
    const close  = $("[data-mobile-close]");
    const nav    = $("[data-mobile-nav]");
    const scrim  = $("[data-scrim]");
    if (!nav) return;
    const open = () => { nav.classList.add("is-open"); scrim.classList.add("is-visible"); document.body.style.overflow = "hidden"; };
    const shut = () => { nav.classList.remove("is-open"); scrim.classList.remove("is-visible"); document.body.style.overflow = ""; };
    toggle?.addEventListener("click", open);
    close?.addEventListener("click", shut);
    scrim?.addEventListener("click", () => { shut(); cartDrawerClose(); });
    // close on link click for mobile
    $$(".mobile-nav a").forEach(a => a.addEventListener("click", () => setTimeout(shut, 60)));
  }

  /* ---------- REVEAL OBSERVER ---------- */
  function reveal() {
    const els = $$(".reveal, [data-fade-up], [data-fade-down], [data-fade-left], [data-fade-right], [data-zoom-in]");
    if (!("IntersectionObserver" in window)) {
      els.forEach(el => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const stagger = parseInt(entry.target.dataset.revealStagger || "0", 10);
          const delay = parseInt(entry.target.dataset.revealDelay || "0", 10);
          setTimeout(() => entry.target.classList.add("is-in"), delay + (stagger * 80));
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
    els.forEach(el => io.observe(el));

    const split = $(".rvw__split");
    if (split) {
      const sio = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { entry.target.classList.add("is-in"); sio.disconnect(); }
      }, { threshold: 0.4 });
      sio.observe(split);
    }
  }

  /* ---------- HERO LOAD + PARALLAX ---------- */
  function heroLoad() {
    const hero = $("[data-hero]");
    if (!hero) return;
    requestAnimationFrame(() => hero.classList.add("is-loaded"));

    if (reduceMotion) return;
    const media = $(".hero__media", hero);
    const inner = $(".hero__inner", hero);
    if (!media) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) { ticking = false; return; }
        const scroll = Math.max(0, -r.top);
        const max = hero.offsetHeight;
        const ratio = Math.min(1, scroll / max);
        media.style.transform = `scale(${1.04 + ratio * 0.04}) translate3d(0, ${ratio * 32}px, 0)`;
        if (inner) inner.style.transform = `translate3d(0, ${ratio * -16}px, 0)`;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- SCROLL PROGRESS BAR ---------- */
  function scrollProgress() {
    const bar = $("[data-scroll-progress]");
    if (!bar) return;
    let ticking = false;
    const update = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const pct = total > 0 ? (window.scrollY / total) * 100 : 0;
      bar.style.width = pct.toFixed(2) + "%";
      ticking = false;
    };
    window.addEventListener("scroll", () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- AURA CURSOR (zero-delay halo + dot + particle trail) ----------
     - Inner dot and outer halo both follow the cursor with NO lerp (operator preference).
     - A canvas particle layer emits soft accent-colored dots that drift + fade,
       giving a "particle trail" feel behind the rigid halo.
     - The current pointer position is light-smoothed (60Hz rAF readout) so
       sub-pixel jitter doesn't show on the dot. */
  function auraCursor() {
    if (isTouch || reduceMotion) return;
    const root = $("[data-aura-cursor]");
    const halo = $("[data-aura-cursor-halo]"); // the inverting circle
    if (!root || !halo) return;

    // A single smoothed circle: it follows the pointer on an ease-out lerp.
    // The element is a solid white disc and .aura-cursor uses mix-blend-mode:
    // difference, so it inverts whatever is behind it. CSS grows it on hover.
    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let hx = mx, hy = my;            // eased circle position
    let started = false;

    document.addEventListener("mousemove", (e) => {
      mx = e.clientX;
      my = e.clientY;
      // Snap to the first real position so it doesn't glide in from center.
      if (!started) { started = true; hx = mx; hy = my; }
    }, { passive: true });

    function tick() {
      // Higher factor = tighter follow; 0.2 stays smooth but clicks land true.
      hx += (mx - hx) * 0.2;
      hy += (my - hy) * 0.2;
      halo.style.transform = `translate(${hx}px, ${hy}px) translate(-50%, -50%)`;
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    const isInteractive = (t) =>
      t && t.closest && t.closest('a, button, [role="button"], [data-cursor="hover"], [data-cursor-on]');

    document.addEventListener("mouseover", (e) => {
      if (isInteractive(e.target)) root.classList.add("is-on-link");
    });

    document.addEventListener("mouseout", (e) => {
      if (!isInteractive(e.target)) return;
      const related = e.relatedTarget && isInteractive(e.relatedTarget);
      if (!related) root.classList.remove("is-on-link");
    });

    document.addEventListener("mousedown", () => root.classList.add("is-pressing"));
    document.addEventListener("mouseup",   () => root.classList.remove("is-pressing"));

    window.addEventListener("blur",  () => root.classList.add("is-hidden"));
    window.addEventListener("focus", () => root.classList.remove("is-hidden"));
  }

  /* ---------- LENIS SCROLL SMOOTHER (optimist.clothing-style) ---------- */
  function smoothScroll() {
    if (isTouch || reduceMotion) return;
    if (typeof window.Lenis === "undefined") return;
    const lenis = new window.Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),  // expoOut, classic Lenis default
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
      orientation: "vertical",
      lerp: 0.10,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Expose for other modules (hero parallax + scroll progress already use rAF and respect document scroll position).
    window.__auraLenis = lenis;
  }

  /* ---------- MAGNETIC BUTTONS ---------- */
  function magneticButtons() {
    if (isTouch || reduceMotion) return;
    const els = $$(".btn--lg, .btn--magnet, .header__action-btn");
    els.forEach(el => {
      let raf = null;
      el.classList.add("btn--magnet");
      el.addEventListener("mousemove", (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.transform = `translate(${x * 0.18}px, ${y * 0.22}px)`;
        });
      });
      el.addEventListener("mouseleave", () => {
        if (raf) cancelAnimationFrame(raf);
        el.style.transform = "";
      });
    });
  }

  /* ---------- TILT 3D CARDS ---------- */
  function tilt3d() {
    if (isTouch || reduceMotion) return;
    const cards = $$(".ptile, .rev__card, .pdp-glucose__card");
    cards.forEach(card => {
      let raf = null;
      card.addEventListener("mousemove", (e) => {
        const r = card.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.setProperty("--mx", `${x}%`);
          card.style.setProperty("--my", `${y}%`);
          const rx = (y - 50) / 25;
          const ry = (x - 50) / -25;
          card.style.transform = `perspective(1200px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-8px)`;
        });
      });
      card.addEventListener("mouseleave", () => {
        if (raf) cancelAnimationFrame(raf);
        card.style.transform = "";
      });
    });
  }

  /* ---------- COUNT UP ---------- */
  function countUp() {
    const els = $$("[data-count-up]");
    if (!els.length || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.dataset.countUp);
        const dur = parseInt(el.dataset.countDuration || "1400", 10);
        const decimals = parseInt(el.dataset.countDecimals || "0", 10);
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(el => io.observe(el));
  }

  /* ---------- ACTIVE NAV LINK ---------- */
  function activeNav() {
    const path = location.pathname.replace(/\/$/, "");
    $$(".header__nav a, .mobile-nav a").forEach(a => {
      try {
        const aPath = new URL(a.href, location.href).pathname.replace(/\/$/, "");
        if (aPath && aPath !== "" && (path === aPath || (aPath !== "/" && path.indexOf(aPath) === 0))) {
          a.classList.add("is-active");
        }
      } catch (e) {}
    });
  }

  /* ---------- IMAGE FADE-IN ---------- */
  function imageFade() {
    $$("img").forEach(img => {
      if (img.complete) {
        img.classList.add("is-loaded");
      } else {
        img.addEventListener("load", () => img.classList.add("is-loaded"), { once: true });
        img.addEventListener("error", () => img.classList.add("is-loaded"), { once: true });
      }
    });
  }

  /* ---------- NEWSLETTER ---------- */
  function newsletter() {
    $$("[data-newsletter]").forEach(form => {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const msg = $("[data-newsletter-msg]", form);
        const fd  = new FormData(form);
        const email = fd.get("contact[email]");
        if (msg) msg.textContent = "Sending...";
        try {
          const klaviyoKey = document.documentElement.dataset.klaviyo;
          if (klaviyoKey) {
            await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyoKey}`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "revision": "2024-10-15" },
              body: JSON.stringify({ data: { type: "subscription", attributes: { profile: { data: { type: "profile", attributes: { email } } } } } })
            });
          } else {
            const r = await fetch("/contact#newsletter", { method: "POST", body: fd });
            if (!r.ok) throw new Error("submit failed");
          }
          const row = form.querySelector(".nl__row");
          if (row) row.style.display = "none";
          if (msg) msg.textContent = "Welcome. We will send you the sleep & recovery primer first.";
        } catch (err) {
          if (msg) msg.textContent = "Something went wrong. Please try again.";
        }
      });
    });
  }

  /* ---------- CART DRAWER ---------- */
  let cartDrawerEl;
  function cartDrawerOpen() {
    cartDrawerEl = cartDrawerEl || $("[data-cart-drawer]");
    if (!cartDrawerEl) return;
    cartDrawerEl.classList.add("is-open");
    cartDrawerEl.setAttribute("aria-hidden", "false");
    $("[data-scrim]")?.classList.add("is-visible");
    document.body.style.overflow = "hidden";
  }
  function cartDrawerClose() {
    cartDrawerEl = cartDrawerEl || $("[data-cart-drawer]");
    if (!cartDrawerEl) return;
    cartDrawerEl.classList.remove("is-open");
    cartDrawerEl.setAttribute("aria-hidden", "true");
    $("[data-scrim]")?.classList.remove("is-visible");
    document.body.style.overflow = "";
  }
  async function cartFetch() {
    const r = await fetch(`${window.theme.routes.cart_url}.js`, { headers: { "Accept": "application/json" } });
    return r.json();
  }
  function cartUpdateBadge(count) {
    const b = $("[data-cart-count]");
    if (!b) return;
    b.textContent = count;
    b.classList.add("is-bouncing");
    setTimeout(() => b.classList.remove("is-bouncing"), 540);
  }
  async function cartRender() {
    const cart = await cartFetch();
    cartUpdateBadge(cart.item_count);
    const drawer = $("[data-cart-drawer]");
    if (!drawer) return;
    const items = $("[data-cart-items]", drawer);
    if (cart.item_count === 0) {
      items.innerHTML = `<p class="t-body" style="color: var(--text-soft);">Your bag is empty.</p>`;
    } else {
      items.innerHTML = cart.items.map((line, idx) => `
        <div class="cart-line" data-key="${line.key}" style="animation-delay:${idx * 60}ms;">
          <div class="cart-line__img">
            ${line.image ? `<img src="${line.image.replace(/(\.[a-z]+)$/i, "_160x160$1")}" width="80" height="80" alt="">` : ""}
          </div>
          <div>
            <div class="cart-line__title">${line.product_title}</div>
            <div class="cart-line__variant">${line.variant_title || ""}</div>
            <div class="qty-stepper">
              <button type="button" data-cart-dec aria-label="Decrease">${minusIcon()}</button>
              <input type="text" inputmode="numeric" value="${line.quantity}" data-cart-qty>
              <button type="button" data-cart-inc aria-label="Increase">${plusIcon()}</button>
            </div>
            <button type="button" class="cart-line__remove" data-cart-remove>Remove</button>
          </div>
          <div class="cart-line__price">${money(line.final_line_price)}</div>
        </div>
      `).join("");
    }
    const sub = $("[data-cart-subtotal]", drawer);
    if (sub) sub.textContent = money(cart.total_price);
  }
  async function cartAdd(formData) {
    const r = await fetch(window.theme.routes.cart_add_url + ".js", {
      method: "POST", headers: { "Accept": "application/json" }, body: formData
    });
    if (!r.ok) throw new Error("Add to cart failed");
    return r.json();
  }
  async function cartChange(key, quantity) {
    const r = await fetch(window.theme.routes.cart_change_url + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ id: key, quantity })
    });
    return r.json();
  }
  function plusIcon()  { return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`; }
  function minusIcon() { return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>`; }

  function cart() {
    $$("[data-cart-toggle]").forEach(el => {
      el.addEventListener("click", (e) => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        cartDrawerOpen();
      });
    });
    $("[data-cart-close]")?.addEventListener("click", cartDrawerClose);

    document.addEventListener("click", async (e) => {
      const t = e.target.closest("[data-cart-remove], [data-cart-dec], [data-cart-inc]");
      if (!t) return;
      const line = t.closest(".cart-line");
      if (!line) return;
      const key = line.dataset.key;
      const qtyEl = $("[data-cart-qty]", line);
      let q = parseInt(qtyEl?.value || "1", 10);
      if (t.matches("[data-cart-remove]")) q = 0;
      if (t.matches("[data-cart-dec]"))    q = Math.max(0, q - 1);
      if (t.matches("[data-cart-inc]"))    q = q + 1;
      await cartChange(key, q);
      await cartRender();
    });
  }

  /* ---------- PRODUCT FORM ---------- */
  function productForm() {
    const form = $("[data-product-form]");
    if (!form) return;
    const dataEl = $("[data-product-json]");
    if (!dataEl) return;
    let product;
    try { product = JSON.parse(dataEl.textContent); } catch { return; }

    const selected = {};
    product.options.forEach((name, idx) => {
      const v = product.variants[0]?.options[idx];
      if (v) selected[name] = v;
    });
    const v0 = product.variants.find(v => v.id === product.selected_or_first_available_variant?.id) || product.variants.find(v => v.available) || product.variants[0];
    if (v0) {
      product.options.forEach((name, idx) => { selected[name] = v0.options[idx]; });
    }

    function findVariant() {
      return product.variants.find(v => product.options.every((name, idx) => v.options[idx] === selected[name]));
    }

    // The currently-selected color value, whatever the option happens to be named.
    function selectedColor() {
      const name = product.options.find(n => /colou?r/i.test(n));
      return name ? selected[name] : null;
    }

    // Best image for the current selection: the variant's own image first,
    // then any product image whose alt text mentions the chosen color.
    function variantImageSrc(v) {
      if (v && v.featured_image && v.featured_image.src) return v.featured_image.src;
      const color = (selectedColor() || "").toLowerCase();
      if (color) {
        const media = (product.media || []).find(m => (m.alt || "").toLowerCase().includes(color));
        if (media && media.src) return media.src;
      }
      return null;
    }

    // Request a gallery-sized render from the Shopify CDN.
    function sizedImage(src) {
      if (!src) return src;
      let u = src.startsWith("//") ? "https:" + src : src;
      return u + (u.includes("?") ? "&" : "?") + "width=1200";
    }

    function updateGallery(v) {
      const src = variantImageSrc(v);
      if (!src) return;
      swapMainImage(sizedImage(src));
      const stickyThumb = $("[data-sticky-buy] img");
      if (stickyThumb) {
        let u = src.startsWith("//") ? "https:" + src : src;
        stickyThumb.src = u + (u.includes("?") ? "&" : "?") + "width=96";
      }
    }

    function updateUI(switchImage) {
      $$("[data-option-button]", form).forEach(btn => {
        const name = btn.dataset.optionName;
        const value = btn.dataset.optionValue;
        const isSelected = selected[name] === value;
        btn.classList.toggle("is-selected", isSelected);
        if (btn.getAttribute("role") === "radio") btn.setAttribute("aria-checked", isSelected ? "true" : "false");
      });
      $$("[data-opt-current]", form).forEach(span => {
        span.textContent = selected[span.dataset.optCurrent] || "";
      });
      const v = findVariant();
      const idInput = $("[data-variant-id]", form);
      const addBtn  = $("[data-add]", form);
      const addLabel = $("[data-add-label]", form);
      const priceEl = $("[data-price]");
      const stickyPrice = $("[data-sticky-price]");
      if (v) {
        if (idInput) idInput.value = v.id;
        if (addBtn) addBtn.disabled = !v.available;
        const price = money(v.price);
        if (priceEl) priceEl.textContent = price;
        if (stickyPrice) stickyPrice.textContent = price;
        if (addLabel) addLabel.textContent = v.available
          ? `Add to cart  ·  ${price}`
          : `Sold out`;
      } else {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = "Sold out";
      }
      if (switchImage) updateGallery(v);
    }

    form.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-option-button]");
      if (!btn) return;
      e.preventDefault();
      selected[btn.dataset.optionName] = btn.dataset.optionValue;
      updateUI(true);
    });

    // Fallback <select> for any option that isn't rendered as bubbles.
    $$("[data-option-select]", form).forEach(sel => {
      sel.addEventListener("change", () => {
        selected[sel.dataset.optionSelect] = sel.value;
        updateUI(true);
      });
    });

    const qtyInput = $("[data-qty]", form);
    $("[data-qty-inc]", form)?.addEventListener("click", () => { if (qtyInput) qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) + 1); });
    $("[data-qty-dec]", form)?.addEventListener("click", () => { if (qtyInput) qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) - 1); });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const v = findVariant();
      if (!v || !v.available) return;
      const fd = new FormData();
      fd.set("id", v.id);
      fd.set("quantity", qtyInput?.value || "1");
      try {
        await cartAdd(fd);
        await cartRender();
        cartDrawerOpen();
      } catch (err) { console.error(err); }
    });

    $("[data-sticky-add]")?.addEventListener("click", async () => {
      const v = findVariant();
      if (!v || !v.available) return;
      const fd = new FormData();
      fd.set("id", v.id);
      fd.set("quantity", "1");
      try {
        await cartAdd(fd);
        await cartRender();
        cartDrawerOpen();
      } catch (err) { console.error(err); }
    });

    updateUI();
  }

  /* ---------- PRODUCT GALLERY (thumb / variant -> main) ---------- */
  // Compare image URLs ignoring protocol + query (CDN width/version params),
  // so the same source at different sizes counts as a match.
  const sameImage = (a, b) =>
    (a || "").replace(/^https?:/, "").split("?")[0] ===
    (b || "").replace(/^https?:/, "").split("?")[0];

  // Shared fade-swap used by both thumbnail clicks and color/variant changes.
  function swapMainImage(newSrc) {
    const main = $("[data-gallery-main] img");
    if (!main || !newSrc) return;
    if (sameImage(main.currentSrc || main.src, newSrc)) return;
    const wrap = main.closest("[data-gallery-main]") || main.parentElement;
    wrap.classList.add("is-changing");
    // Clear srcset so the new src wins regardless of viewport size
    main.removeAttribute("srcset");
    main.removeAttribute("sizes");
    const apply = () => {
      main.src = newSrc;
      main.addEventListener("load", () => wrap.classList.remove("is-changing"), { once: true });
      main.addEventListener("error", () => wrap.classList.remove("is-changing"), { once: true });
      // safety fallback: never leave is-changing stuck
      setTimeout(() => wrap.classList.remove("is-changing"), 1200);
    };
    // small delay so the fade-out is visible
    setTimeout(apply, 160);
    // keep the active thumbnail in sync if one matches the new image
    $$("[data-thumb]").forEach(t =>
      t.classList.toggle("is-active", sameImage(t.dataset.src, newSrc))
    );
  }

  function productGallery() {
    const main = $("[data-gallery-main] img");
    const thumbs = $$("[data-thumb]");
    if (!main || !thumbs.length) return;
    thumbs.forEach(t => {
      t.addEventListener("click", () => swapMainImage(t.dataset.src));
    });
  }

  /* ---------- HOME / COLLECTION TILE SWATCHES (color -> tile image) ---------- */
  function tileSwatches() {
    const groups = $$("[data-tile-swatches]");
    if (!groups.length) return;
    groups.forEach(group => {
      const tile = group.closest(".ptile");
      const img = tile && $(".ptile__viewer img", tile);
      if (!img) return;
      const swatches = $$("[data-tile-swatch]", group);
      swatches.forEach(sw => {
        sw.addEventListener("click", (e) => {
          // swatches live outside the tile's <a>, but guard anyway
          e.preventDefault();
          swatches.forEach(s => { s.classList.remove("is-active"); s.setAttribute("aria-pressed", "false"); });
          sw.classList.add("is-active");
          sw.setAttribute("aria-pressed", "true");
          const src = sw.dataset.src;
          if (!src || sameImage(img.currentSrc || img.src, src)) return;
          const wrap = img.closest(".ptile__viewer") || img.parentElement;
          wrap.classList.add("is-changing");
          img.removeAttribute("srcset");
          img.removeAttribute("sizes");
          const done = () => wrap.classList.remove("is-changing");
          setTimeout(() => {
            img.src = src;
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
            setTimeout(done, 1200);
          }, 120);
        });
      });
    });
  }

  /* ---------- STICKY BUY BAR ---------- */
  function stickyBuy() {
    const bar = $("[data-sticky-buy]");
    if (!bar) return;
    const buy = $("[data-buy]");
    if (!buy) return;
    const io = new IntersectionObserver(([entry]) => {
      bar.classList.toggle("is-visible", !entry.isIntersecting);
    }, { rootMargin: "0px 0px -100% 0px", threshold: 0 });
    io.observe(buy);
  }

  /* ---------- STICKY SHOP BAR (home page mobile) ---------- */
  function stickyShop() {
    const bar = $("[data-sticky-shop]");
    if (!bar) return;
    const trigger = $(".vps");
    if (!trigger || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      const past = entry.boundingClientRect.bottom < 0;
      bar.classList.toggle("is-visible", past);
    }, { threshold: 0 });
    io.observe(trigger);
  }

  /* ---------- HEADER SHOP CTA (home page desktop) ---------- */
  function headerShopCta() {
    const cta = $("[data-header-shop-cta]");
    if (!cta) return;
    const hero = $(".hero");
    if (!hero || !("IntersectionObserver" in window)) {
      cta.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      cta.classList.toggle("is-visible", !entry.isIntersecting);
    }, { threshold: 0 });
    io.observe(hero);
  }

  /* ---------- SHOP MEGAMENU ---------- */
  function megamenu() {
    const root = $("[data-megamenu]");
    if (!root) return;
    const trigger = $("[data-megamenu-trigger]", root);
    if (!trigger) return;
    const open = () => { root.classList.add("is-open"); trigger.setAttribute("aria-expanded", "true"); };
    const close = () => { root.classList.remove("is-open"); trigger.setAttribute("aria-expanded", "false"); };
    const toggle = () => { root.classList.contains("is-open") ? close() : open(); };

    trigger.addEventListener("click", (e) => { e.preventDefault(); toggle(); });
    if (!isTouch) {
      root.addEventListener("mouseenter", open);
      root.addEventListener("mouseleave", close);
    }
    document.addEventListener("click", (e) => {
      if (!root.contains(e.target)) close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
    });
  }

  /* ---------- SCROLLY CAPTIONS (pinned 3D sequence) ---------- */
  // Drives the caption cross-fades for any [data-scrolly] pinned section and
  // publishes scroll progress as --scrolly-p (so CSS can react even when the
  // 3D viewer falls back to a static image). The ring transform itself is
  // owned by aura-3d-viewer.js; this only handles the captions + progress var.
  function scrollyCaptions() {
    const scenes = $$("[data-scrolly]");
    if (!scenes.length) return;
    scenes.forEach((scene) => {
      const caps = $$("[data-cap]", scene);
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = scene.getBoundingClientRect();
        const winH = window.innerHeight || 800;
        const total = r.height - winH;
        const p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
        scene.style.setProperty("--scrolly-p", p.toFixed(4));
        if (caps.length) {
          const idx = Math.min(caps.length - 1, Math.floor(p * caps.length * 0.999));
          caps.forEach((c, i) => c.classList.toggle("is-active", i === idx));
        }
      };
      window.addEventListener("scroll", () => {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      }, { passive: true });
      window.addEventListener("resize", update, { passive: true });
      update();
    });
  }

  /* ---------- BOOT ---------- */
  themeToggle();
  preloader();
  document.addEventListener("DOMContentLoaded", () => {
    pageTransitions();
    announcement();
    header();
    mobileNav();
    reveal();
    heroLoad();
    scrollyCaptions();
    scrollProgress();
    smoothScroll();
    auraCursor();
    magneticButtons();
    tilt3d();
    countUp();
    activeNav();
    imageFade();
    newsletter();
    cart();
    productForm();
    productGallery();
    tileSwatches();
    stickyBuy();
    stickyShop();
    headerShopCta();
    megamenu();
  });
})();
